import { defineStore } from 'pinia'
import AuthService from '@/plugins/api-plugin/services/auth.service'
import router from '@/router'
import { ApiPluginInterface } from '@/plugins/api-plugin'

export const useAuthStore = defineStore({
    id: 'auth',
    state: () => ({
        accessToken: AuthService.getAuthToken(),
        isAuth: AuthService.isAuth(),
        roles: [] as string[],
        /** The roles request has ended, with roles or without. */
        rolesLoaded: false,
        rolesRequest: null as Promise<void> | null,
    }),
    actions: {
        async login(email: string, password: string, api?: ApiPluginInterface) {
            const response = await api?.user.signIn(email, password)
            if (response?.accessToken && response?.refreshToken) {
                AuthService.setAuthTokens(response?.accessToken, response?.refreshToken)
                this.accessToken = response?.accessToken
                this.isAuth = true
                await router.push({ name: 'home' })
            }
        },
        async logout() {
            if (AuthService.getJwtFromCookie()) {
                AuthService.logout()
                AuthService.coLogout()
            } else {
                AuthService.logout()
                this.accessToken = null
                this.isAuth = false
                await router.push({ name: 'sign.in' })
            }
        },
        /**
         * Loads the roles once per session: a call while the request runs shares it, and a failed
         * request is made again on the next call. `reload` always asks the server.
         */
        loadRoles(api?: ApiPluginInterface, reload = false): Promise<void> {
            if (!reload && this.roles.length) return Promise.resolve()
            if (!reload && this.rolesRequest) return this.rolesRequest
            const request = (async () => {
                try {
                    this.roles = (await api?.common.getRoles()) || []
                } catch (failure) {
                    this.rolesRequest = null
                    throw failure
                } finally {
                    this.rolesLoaded = true
                }
            })()
            this.rolesRequest = request
            return request
        },
        isUserHasRole(role: string | string[]): boolean {
            if (typeof role === 'string') {
                return this.roles.includes(role)
            }
            return role.every((r) => this.roles.includes(r))
        },
    },
})
